import React from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, makeStyles, Paper, Typography,
} from '@material-ui/core';
import { displaySuccess, displayErrorMessage } from 'Helpers/FacetNotificationManager';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { SaveRounded } from '@material-ui/icons';
import NakedInput from './NakedInput';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 0, 2, 0),
    width: theme.dimensions.md,
    margin: theme.spacing(1),
  },
  row: {
    padding: theme.spacing(0, 2, 0, 2),
    display: 'flex',
    justifyContent: 'space-between',
    width: theme.dimensions.md,
    '&:nth-child(even)': {
      backgroundColor: theme.palette.facet.lightGray,
    },
    '&:last-child': {
      marginTop: 'auto',
    },
  },
  content: {
    marginTop: theme.spacing(1),
  },
  subtitle: {
    flexGrow: 1,
  },
  input: {
    textAlign: 'end',
    width: 64,
  },
  [theme.breakpoints.down('sm')]: {
    root: {
      width: '100%',
    },
    row: {
      width: '100%',
    },
  },
}));

export default function SnapshotIncome({
  clientCollection,
  setTotalIncome,
  total,
}) {
  const classes = useStyles();
  const [hasChanged, setHasChanged] = React.useState(false);
  const [clients, setClients] = React.useState(
    clientCollection.resources().list(),
  );

  const updateTotal = (resources) => {
    setTotalIncome(
      resources.reduce((acc, next) => {
        if (!next) return 0;
        return parseInt(next.attr('netMonthlyIncome') || 0, 10) + acc;
      }, 0),
    );
  };

  const handleChange = (index, value) => {
    const v = value || 0;
    const client = clients[index];
    client.attr('netMonthlyIncome', v);
    const next = [...clients];
    next.splice(index, 1, client);

    setHasChanged(true);
    setClients(next);
    updateTotal(next);
  };

  const handleSave = async () => {
    try {
      // sequentially update each net monthly income to avoid parallel request deadlock
      for (const client of clients) {
        // eslint-disable-next-line no-await-in-loop
        await client.submit();
      }
      setHasChanged(false);
      clientCollection.refresh().then(() => {
        setClients(clientCollection.resources().list());
      }).then(() => {
        displaySuccess('Net monthly income saved!');
      });
    } catch (e) {
      displayErrorMessage(e, e.errors);
    }
  };

  React.useEffect(() => {
    updateTotal(clients);
  }, []);

  return (
    <Paper elevation={2} className={classes.root}>
      <div className={classes.row}>
        <Typography unselectable variant="h3" color="primary">
          Net Monthly Income
        </Typography>
        <IconButton
          disabled={!hasChanged}
          onClick={handleSave}
          color="primary"
          size="small"
          test="net-monthly-income-save-button"
        >
          <SaveRounded />
        </IconButton>
      </div>
      <div className={classes.content}>
        {clients.map((c, idx) => {
          const field = c.meta().field('netMonthlyIncome');
          field.attr('_component', NakedInput);

          return (
            <div className={classes.row} key={`snapshot-client-${c.id()}`} test={`net-monthly-income-${c.id()}`}>
              <Typography variant="body1" className={classes.subtitle} test={`net-monthly-income-title-${c.id()}`}>{`${c.attr('firstName')} ${c.attr('lastName')}`}</Typography>
              <MetaControl
                test={`net-monthly-income-value-${c.id()}`}
                attrName="netMonthlyIncome"
                className={classes.editable}
                onChange={v => handleChange(idx, v)}
                resource={c}
                value={c.attr('netMonthlyIncome')}
                classes={{ input: classes.input }}
                startAdornment={<span>$</span>}
              />
            </div>
          );
        })}
      </div>
      <div className={classes.row}>
        <Typography unselectable variant="body1" color="primary">
          Total
        </Typography>
        <Typography unselectable variant="body1" color="primary">
          ${Number.isNaN(total) ? 0 : total.toLocaleString()}
        </Typography>
      </div>
    </Paper>
  );
}

SnapshotIncome.propTypes = {
  clientCollection: PropTypes.object.isRequired,
  setTotalIncome: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
};
